/**
 * Pick one country — the nationality field. Looks like a select box (flag + name
 * + chevron) and opens the same ./CountryPicker the phone field uses, without
 * the dial codes.
 *
 * The value is the ISO-3166 alpha-2 code (`TH`), never a display name: names are
 * localised (`ไทย` / `Thailand`), and a stored attribute must not depend on the
 * language the applicant happened to read the form in. Nothing is preselected —
 * a default would be the form asserting a nationality on the applicant's behalf.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Chevrons, CountryPicker } from './CountryPicker';
import { countries, flagUrl } from './phoneNumber';

interface CountrySelectProps {
  /** ISO alpha-2 code, or '' when nothing is chosen yet. */
  value: string;
  onChange: (code: string) => void;
  /** Accessible name (the visible label lives with the caller). */
  label: string;
  /** Shown while empty, and as the sheet's heading. */
  placeholder: string;
  name?: string;
  disabled?: boolean;
}

export function CountrySelect({
  value,
  onChange,
  label,
  placeholder,
  name,
  disabled,
}: CountrySelectProps) {
  const { i18n } = useTranslation();
  const list = useMemo(() => countries(i18n.language), [i18n.language]);
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);
  const chosen = list.find((c) => c.code === value);

  return (
    <div className="country-select" ref={root}>
      <button
        type="button"
        name={name}
        className={`details-input country-select__trigger${chosen ? '' : ' is-empty'}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={chosen ? `${label}: ${chosen.name}` : label}
        data-track="country_selector"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
      >
        {chosen ? <img src={flagUrl(chosen.code)} alt="" width={22} height={22} /> : null}
        <span className="country-select__value">{chosen ? chosen.name : placeholder}</span>
        <Chevrons />
      </button>
      <CountryPicker
        open={open}
        onClose={close}
        selected={value}
        onChoose={(code) => {
          onChange(code);
          setOpen(false);
        }}
        anchor={root}
        showDial={false}
        title={placeholder}
      />
    </div>
  );
}
